import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface LeadStatus {
    id: string;
    name: string;
    label: string;
    color: string;
    is_system: boolean;
    sort_order: number;
    required_contact_attempts?: number;
    is_active: boolean;
    created_by?: string;
    updated_by?: string;
    created_at: string;
    updated_at: string;
    usage_count?: number;
}

export interface CreateLeadStatusRequest {
    name: string;
    label: string;
    color: string;
    sort_order?: number;
    required_contact_attempts?: number;
}

export interface UpdateLeadStatusRequest {
    name?: string;
    label?: string;
    color?: string;
    sort_order?: number;
    required_contact_attempts?: number;
}

export interface ReorderStatusRequest {
    order: Array<{
        id: string;
        sort_order: number;
    }>;
}

// Get all lead statuses with usage counts
export const useLeadStatuses = () => {
    return useQuery<ApiResponse<LeadStatus[]>>({
        queryKey: ['lead-statuses'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<LeadStatus[]>>(
                API_ENDPOINTS.CRM.LEAD_STATUSES
            );
            return data;
        },
    });
};

// Get active lead statuses (for forms and filters)
export const useActiveLeadStatuses = () => {
    return useQuery<ApiResponse<LeadStatus[]>>({
        queryKey: ['lead-statuses', 'active'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<LeadStatus[]>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/active`
            );
            return data;
        },
    });
};

// Get a specific lead status
export const useLeadStatus = (id: string) => {
    return useQuery<ApiResponse<LeadStatus>>({
        queryKey: ['lead-statuses', id],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<LeadStatus>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/${id}`
            );
            return data;
        },
        enabled: !!id,
    });
};

// Create a new lead status
export const useCreateLeadStatus = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (statusData: CreateLeadStatusRequest) => {
            const { data } = await httpClient.post<ApiResponse<LeadStatus>>(
                API_ENDPOINTS.CRM.LEAD_STATUSES,
                statusData
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            enqueueSnackbar('Lead status created successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to create lead status';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};

// Update a lead status
export const useUpdateLeadStatus = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: UpdateLeadStatusRequest }) => {
            const response = await httpClient.put<ApiResponse<LeadStatus>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/${id}`,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            enqueueSnackbar('Lead status updated successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to update lead status';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};

// Toggle lead status active/inactive
export const useToggleLeadStatus = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (id: string) => {
            const { data } = await httpClient.post<ApiResponse<LeadStatus>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/${id}/toggle`
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            enqueueSnackbar('Lead status toggled successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to toggle lead status';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};

// Reorder lead statuses
export const useReorderLeadStatuses = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (orderData: ReorderStatusRequest) => {
            const { data } = await httpClient.post<ApiResponse<void>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/reorder`,
                orderData
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            enqueueSnackbar('Lead statuses reordered successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to reorder lead statuses';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};

// Seed default lead statuses
export const useSeedDefaultLeadStatuses = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async () => {
            const { data } = await httpClient.post<ApiResponse<void>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/seed-defaults`
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            enqueueSnackbar('Default lead statuses seeded successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to seed default lead statuses';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};

// Delete a lead status (with or without replacement)
export const useDeleteLeadStatus = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({
            id,
            replacementStatusId,
        }: {
            id: string;
            replacementStatusId?: string;
        }) => {
            const requestData = replacementStatusId
                ? { replacement_status_id: replacementStatusId }
                : {};
            const { data } = await httpClient.delete<ApiResponse<any>>(
                `${API_ENDPOINTS.CRM.LEAD_STATUSES}/${id}`,
                { data: requestData }
            );
            return data;
        },
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: ['lead-statuses'] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });

            const results = data.data;
            if (results.affected_leads > 0) {
                enqueueSnackbar(
                    `Lead status deleted successfully. ${results.affected_leads} leads moved to "${results.replacement_status.label}"`,
                    { variant: 'success' }
                );
            } else {
                enqueueSnackbar('Lead status deleted successfully', { variant: 'success' });
            }
        },
        onError: (error: any) => {
            const message = error.response?.data?.message || 'Failed to delete lead status';
            enqueueSnackbar(message, { variant: 'error' });
        },
    });
};
